/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect } from 'react';

// Store
import {
	TParams,
	TQueryParams,
	TSelectOption,
} from '@global-types/index';
import useStore from '@store/index';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TMeasurabilityProjectQueryParams } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useMeasurabilityProject = () => {
	const {
		setMeasurabilityProjectsQueryParams,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		setMeasurabilityProjectsPageSize,
		setMeasurabilityProjectsPage,
		addMeasurabilityProjectFormOpened,
		setAddMeasurabilityProjectFormOpened,
		measurabilityProjectsSortParams,
	} = useStore();

	const handleActiveFilter = (status: TQueryParams[]) => {
		setMeasurabilityProjectsPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setMeasurabilityProjectsQueryParams({
			...measurabilityProjectsQueryParams,
			...queryParamsActive,
		});
	};

	/**************************** Pagination ****************************/

	const handleChangePaginationSize = function (
		_size: string | number
	) {
		setMeasurabilityProjectsPageSize(Number(_size));
		setMeasurabilityProjectsPage(1);
	};

	const handleChangePagination = (
		page: string | number
	) => {
		setMeasurabilityProjectsPage(page);
	};

	/**
	 * Filter list by NAME
	 *
	 * @param {TSelectOption} _NAMES
	 */
	const handleProjectNameFilter = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setMeasurabilityProjectsPage(1);
		const queryParamsProjects = {
			name: event.target.value,
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			{
				...measurabilityProjectsQueryParams,
				...queryParamsProjects,
			};
		setMeasurabilityProjectsQueryParams(filterParams);
	};

	const handleSearchProjectNameFilter = (
		name: string
	) => {
		setMeasurabilityProjectsPage(1);
		const queryParamsProjects = {
			name,
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			{
				...measurabilityProjectsQueryParams,
				...queryParamsProjects,
			};
		setMeasurabilityProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by technology
	 *
	 * @param {TSelectOption} _technologies
	 */
	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setMeasurabilityProjectsPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...measurabilityProjectsQueryParams,
				...queryParamsProjects,
			});
		setMeasurabilityProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by country
	 *
	 * @param {TSelectOption} _countries
	 */
	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setMeasurabilityProjectsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(measurabilityProjectsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setMeasurabilityProjectsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...measurabilityProjectsQueryParams,
				...queryParamsCountries,
			});
		setMeasurabilityProjectsQueryParams(filterParams);
	};

	const handleOpenDrawerAddMeasurabilityProject = () => {
		setAddMeasurabilityProjectFormOpened(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setMeasurabilityProjectsQueryParams({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/********************************************************************************************/
	return {
		handleProjectNameFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		handleChangePaginationSize,
		handleChangePagination,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
		setMeasurabilityProjectsPageSize,
		setMeasurabilityProjectsPage,
		addMeasurabilityProjectFormOpened,
		setAddMeasurabilityProjectFormOpened,
		handleOpenDrawerAddMeasurabilityProject,
		handleActiveFilter,
		setMeasurabilityProjectsQueryParams,
		handleSearchProjectNameFilter,
		measurabilityProjectsSortParams,
	};
};
